export class NavigatorPanel {
  private canvas: HTMLCanvasElement;
  private container: HTMLElement;
  private zoomSlider: HTMLInputElement;
  /** Typed zoom-percentage input — matches PS Navigator panel. */
  private zoomInput: HTMLInputElement;
  private dragging = false;
  private onNavigate: ((panX: number, panY: number, zoom: number) => void) | null = null;

  constructor() {
    this.canvas = document.createElement("canvas");
    this.canvas.className = "navigator__canvas";
    this.zoomSlider = document.createElement("input");
    this.zoomSlider.type = "range";
    this.zoomSlider.min = "1";
    this.zoomSlider.max = "3200";
    this.zoomSlider.value = "100";
    this.zoomSlider.className = "navigator__zoom-slider";
    this.zoomInput = document.createElement("input");
    this.zoomInput.type = "number";
    this.zoomInput.min = "1";
    this.zoomInput.max = "3200";
    this.zoomInput.value = "100";
    this.zoomInput.className = "navigator__zoom-input";
    this.zoomInput.title = "Zoom percentage";

    this.container = document.createElement("div");
    this.container.className = "navigator";
    this.container.innerHTML = `<div class="panel-title">Navigator</div>`;
    this.container.appendChild(this.canvas);

    const zoomRow = document.createElement("div");
    zoomRow.className = "navigator__zoom-row";
    zoomRow.appendChild(this.zoomInput);
    zoomRow.appendChild(this.zoomSlider);
    this.container.appendChild(zoomRow);

    this._bindEvents();
  }

  private _bindEvents(): void {
    this.canvas.addEventListener("mousedown", (e) => {
      this.dragging = true;
      this._handleDrag(e);
    });

    document.addEventListener("mousemove", (e) => {
      if (this.dragging) this._handleDrag(e);
    });

    document.addEventListener("mouseup", () => {
      this.dragging = false;
    });

    this.canvas.addEventListener("click", (e) => {
      if (!this._docW || !this._docH) return;
      const rect = this.canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;
      const docX = x * this._docW - this._viewW / 2 / this._zoom;
      const docY = y * this._docH - this._viewH / 2 / this._zoom;
      this._emitNavigate(-docX * this._zoom, -docY * this._zoom, this._zoom);
    });

    this.zoomSlider.addEventListener("input", () => {
      const zoom = parseInt(this.zoomSlider.value) / 100;
      this.zoomInput.value = String(Math.round(zoom * 100));
      if (this.onNavigate && this._docW && this._docH) {
        this._emitNavigate(this._panX, this._panY, zoom);
      }
    });

    // Commit typed zoom on Enter or blur.
    const applyZoomInput = () => {
      const pct = Math.min(3200, Math.max(1, parseInt(this.zoomInput.value) || 100));
      this.zoomInput.value = String(pct);
      this.zoomSlider.value = String(pct);
      const zoom = pct / 100;
      if (this.onNavigate && this._docW && this._docH) {
        this._emitNavigate(this._panX, this._panY, zoom);
      }
    };
    this.zoomInput.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); applyZoomInput(); } });
    this.zoomInput.addEventListener("blur", applyZoomInput);
  }

  private _handleDrag(e: MouseEvent): void {
    if (!this._docW || !this._docH) return;
    const rect = this.canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const docX = x * this._docW - this._viewW / 2 / this._zoom;
    const docY = y * this._docH - this._viewH / 2 / this._zoom;
    this._emitNavigate(-docX * this._zoom, -docY * this._zoom, this._zoom);
  }

  private _emitNavigate(panX: number, panY: number, zoom: number): void {
    if (this.onNavigate) {
      this.onNavigate(panX, panY, zoom);
    }
  }

  private _docW = 0;
  private _docH = 0;
  private _viewW = 0;
  private _viewH = 0;
  private _zoom = 1;
  private _panX = 0;
  private _panY = 0;

  setCallback(onNavigate: (panX: number, panY: number, zoom: number) => void): void {
    this.onNavigate = onNavigate;
  }

  render(
    source: HTMLCanvasElement | ImageData,
    docW: number,
    docH: number,
    viewport: { panX: number; panY: number; zoom: number },
    viewW: number,
    viewH: number,
  ): void {
    this._docW = docW;
    this._docH = docH;
    this._viewW = viewW;
    this._viewH = viewH;
    this._zoom = viewport.zoom;
    this._panX = viewport.panX;
    this._panY = viewport.panY;

    const navWidth = this.canvas.parentElement?.clientWidth || 256;
    const thumbW = navWidth - 8;
    const thumbH = Math.round(thumbW * (docH / docW));
    this.canvas.width = thumbW * window.devicePixelRatio;
    this.canvas.height = thumbH * window.devicePixelRatio;
    this.canvas.style.width = thumbW + "px";
    this.canvas.style.height = thumbH + "px";

    const ctx = this.canvas.getContext("2d")!;
    ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);

    // Draw thumbnail
    if (source instanceof HTMLCanvasElement) {
      ctx.drawImage(source, 0, 0, thumbW, thumbH);
    } else {
      const tmpCanvas = document.createElement("canvas");
      tmpCanvas.width = docW;
      tmpCanvas.height = docH;
      const tmpCtx = tmpCanvas.getContext("2d")!;
      tmpCtx.putImageData(source, 0, 0);
      ctx.drawImage(tmpCanvas, 0, 0, thumbW, thumbH);
    }

    // Draw viewport rectangle
    const scale = thumbW / docW;
    const vx = (-viewport.panX / viewport.zoom) * scale;
    const vy = (-viewport.panY / viewport.zoom) * scale;
    const vw = (viewW / viewport.zoom) * scale;
    const vh = (viewH / viewport.zoom) * scale;

    ctx.strokeStyle = "rgba(255, 0, 0, 0.8)";
    ctx.lineWidth = 1;
    ctx.strokeRect(vx, vy, vw, vh);

    // Update zoom slider and typed input (keep them in sync when zoom changes externally).
    const zoomPct = String(Math.round(viewport.zoom * 100));
    this.zoomSlider.value = zoomPct;
    // Only update the text input when it's not focused (to avoid clobbering user input).
    if (document.activeElement !== this.zoomInput) this.zoomInput.value = zoomPct;
  }

  mount(parent: HTMLElement): void {
    parent.appendChild(this.container);
  }

  destroy(): void {
    this.container.remove();
  }
}
